import { Button } from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { getSourceWeights, saveSourceWeights } from "../../api/axiosApi";
import { SourceWeightsType } from "../../utils/common";
import { ToastContainer } from "react-toastify";
import { errorToast, successToast } from "../../utils/toastNotify";
import { ErrorType } from "../../utils/constants";

const mediaTypes = ["Blogs & CHOWs", "Write-ups/ Exclusive Content", "SPS Podcast/ FSL Podcast", "Books"] as const;

type MediaType = (typeof mediaTypes)[number];

type Weights = Record<MediaType, number>;
type SourceCountMap = Record<MediaType, number>;

export default function SourceWeights() {
    const [weights, setWeights] = useState<Weights>({
        "Blogs & CHOWs": 100,
        "Write-ups/ Exclusive Content": 0,
        "SPS Podcast/ FSL Podcast": 0,
        Books: 0
    });
    const [isAdmin, setIsAdmin] = useState<boolean>(false);

    const mediaKeyMap: Record<MediaType, string> = {
        "Blogs & CHOWs": "blogs",
        "Write-ups/ Exclusive Content": "exclusive_content",
        "SPS Podcast/ FSL Podcast": "sps_podcast/fsl_podcast",
        Books: "books"
    };

    const [totalDocuments, setTotalDocuments] = useState<number>(20);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (media: MediaType, value: string) => {
        const newValue = value === "" ? 0 : Number(value);
        // Prevent decimals
        if (!Number.isInteger(newValue)) return;
        if (isNaN(newValue) || newValue < 0 || newValue > 100) return;

        const editableMedia = mediaTypes.filter(m => m !== "Blogs & CHOWs");
        const newTotal = editableMedia.reduce((sum, m) => sum + (m === media ? newValue : weights[m]), 0);

        const remaining = 100 - newTotal;
        if (remaining < 0) return;

        setWeights(prev => ({
            ...prev,
            [media]: newValue,
            "Blogs & CHOWs": remaining
        }));
    };
    const getSourcesCountMap = (): Record<MediaType, number> => {
        const rawCounts = mediaTypes.map(media => ({
            media,
            raw: (weights[media] / 100) * totalDocuments
        }));

        const floored = rawCounts.map(r => ({
            media: r.media,
            value: Math.floor(r.raw),
            fraction: r.raw - Math.floor(r.raw)
        }));

        let total = floored.reduce((sum, item) => sum + item.value, 0);
        let remainder = totalDocuments - total;

        // Distribute remainder to highest fractional parts
        floored
            .sort((a, b) => b.fraction - a.fraction)
            .forEach(item => {
                if (remainder > 0) {
                    item.value += 1;
                    remainder--;
                }
            });

        const result: Record<MediaType, number> = {} as any;
        floored.forEach(item => {
            result[item.media] = item.value;
        });

        return result;
    };
    const sourceCountMap: SourceCountMap = getSourcesCountMap();
    const totalSources = Object.values(sourceCountMap).reduce((sum, val) => sum + val, 0);
    const getSourceWeightsFunction = async () => {
        const sourceWeightsRes = await getSourceWeights();
        const data = await sourceWeightsRes.data;
        if (data.is_admin === true) setIsAdmin(true);
        else setIsAdmin(false);
        setTotalDocuments(data.max_prefetch_documents);
        // console.log("data: ", data)
        const newWeights: Weights = {
            "Write-ups/ Exclusive Content": Number(data.sources.exclusive_content.percentage),
            "Blogs & CHOWs": Number(data.sources.blogs.percentage),
            "SPS Podcast/ FSL Podcast": Number(data.sources["sps_podcast/fsl_podcast"].percentage),
            Books: Number(data.sources.books.percentage)
        };
        setWeights(newWeights);
    };
    useEffect(() => {
        getSourceWeightsFunction();
    }, []);

    const handleSave = async () => {
        const totalWeight = Object.values(weights).reduce((sum, val) => sum + val, 0);
        const sourceCountMap = getSourcesCountMap();
        const totalSources = Object.values(sourceCountMap).reduce((sum, val) => sum + val, 0);

        if (totalWeight !== 100) {
            setError("Total weight must be exactly 100%.");
            return;
        }

        if (totalSources !== totalDocuments) {
            setError("Total # of sources must equal total prefetch documents.");
            return;
        }
        setError(null);

        const payload: SourceWeightsType = {
            max_prefetch_documents: totalDocuments,
            sources: mediaTypes.reduce(
                (sourcesPayload, media) => {
                    sourcesPayload[mediaKeyMap[media]] = {
                        weightage: sourceCountMap[media],
                        percentage: weights[media]
                    };
                    return sourcesPayload;
                },
                {} as Record<string, { weightage: number; percentage: number }>
            )
        };
        const saveSourceWeightsRes = await saveSourceWeights(payload);
        const data = saveSourceWeightsRes.data;
        if (data.status === "success") {
            successToast("Saved successfully!");
        } else {
            errorToast(ErrorType.UNEXPECTED_ERROR);
        }
    };

    const totalWeight = Object.values(weights).reduce((sum, val) => sum + val, 0);

    return (
        <div className="max-w-3xl mx-auto p-6 min-w-96">
            <h2 className="text-2xl font-bold mb-4 text-center">Distribute Source Weights</h2>

            <div className=" flex mb-6">
                <label className=" w-1/2 font-medium mb-2">Maximum Prefetch Documents</label>
                <input
                    type="number"
                    value={totalDocuments}
                    onChange={e => setTotalDocuments(Number(e.target.value))}
                    min={1}
                    disabled={!isAdmin}
                    onKeyDown={e => {
                        if ([".", "e", "-"].includes(e.key)) e.preventDefault();
                    }}
                    className="w-2/4 px-3 py-2 border rounded text-right"
                />
            </div>

            <div className="flex justify-between font-semibold border-b pb-2 mb-4">
                <span className="w-1/2">Source</span>
                <span className="w-1/4 text-right">%</span>
                <span className="w-1/4 text-right"># Sources</span>
            </div>

            {mediaTypes.map(media => (
                <div key={media} className="flex items-center justify-between mb-4">
                    <label className="w-1/2">{media}</label>
                    <div className="relative w-1/4">
                        <input
                            type="number"
                            value={weights[media] === 0 ? "" : weights[media]}
                            onChange={e => handleChange(media, e.target.value)}
                            disabled={media === "Blogs & CHOWs" || !isAdmin}
                            className={`w-full py-2 pr-6 pl-3 border rounded text-right ${media === "Blogs & CHOWs" ? "bg-gray-100 text-gray-500" : ""}`}
                            min={0}
                            max={100}
                            step={1}
                            onKeyDown={e => {
                                if ([".", "e", "-"].includes(e.key)) e.preventDefault();
                            }}
                            placeholder="0"
                        />
                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">%</span>
                    </div>

                    <span className="w-1/4 text-right">{sourceCountMap[media]}</span>
                </div>
            ))}

            <div className="flex justify-between font-semibold mt-2 border-t pt-2">
                <span className="w-1/2 text-left">Total:</span>
                <div className="relative w-1/4">
                    <input
                        type="number"
                        value={totalWeight}
                        disabled
                        className={`w-full py-2 pr-6 pl-3 border rounded text-right text-gray-500" `}
                        placeholder="0"
                    />
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">%</span>
                </div>
                <span className="w-1/4 text-right">{totalSources}</span>
            </div>

            {error && <p className="text-red-600 text-center mt-4 font-medium">{error}</p>}

            <div className="flex justify-center mt-6">
                <Button onClick={handleSave} disabled={!isAdmin} className="bg-blue-400 text-white font-semibold px-6 py-2 rounded ">
                    Save
                </Button>
            </div>
            <ToastContainer toastStyle={{ marginTop: "8vh" }} />
        </div>
    );
}
